/**
 * The split's layout rule (1.I-2 deliverable 15), pure. Two panes sit side by side when the
 * viewport is at least `LAYOUT.splitTabsBelow` wide and the content region holds two minimum
 * panes and the handle between them; otherwise they are two tabs.
 */
import { LAYOUT } from '@codex/ui';

/** The handle between the panes, px (matches `.cx-nav-split__handle`). */
export const HANDLE_WIDTH = 12;

export type SplitForm = 'side-by-side' | 'tabs';

/** `region` is the content region's width, 0 while it is not measured yet. */
export function splitLayout(viewport: number, region: number): SplitForm {
  if (viewport < LAYOUT.splitTabsBelow) return 'tabs';
  if (region > 0 && region < LAYOUT.splitPaneMin * 2 + HANDLE_WIDTH) return 'tabs';
  return 'side-by-side';
}

/** The widest pane 1 can be while pane 2 keeps its minimum. */
export function firstPaneMax(region: number): number {
  return Math.max(LAYOUT.splitPaneMin, region - HANDLE_WIDTH - LAYOUT.splitPaneMin);
}

/** Pane 1's width: the wanted one (half the region by default) held between the two minimums. */
export function clampFirstWidth(wanted: number | null, region: number): number {
  const base = wanted ?? Math.round((region - HANDLE_WIDTH) / 2);
  return Math.min(firstPaneMax(region), Math.max(LAYOUT.splitPaneMin, Math.round(base)));
}

/**
 * The element that takes focus once the palette's "Swap to other pane" has made `to` the active
 * pane: the pane itself side by side, its tab in tabs. The palette's own return focus would put
 * focus back into the pane that opened it, and that focus would make that pane active again.
 */
export function focusIdAfterSwap(form: SplitForm, to: 'main' | 'split'): string {
  return form === 'tabs' ? `cx-nav-tab-${to}` : `cx-nav-panel-${to}`;
}
